import { ReactNode } from 'react'

import isEmpty from 'lodash/isEmpty'

import { ResourceLink } from 'components/Links/ResourceLink'
import { UNDEFINED_VALUE } from 'const/formatting'
import { isUnset } from 'helpers/utilities'
import { labelUrl, productDetailUrl } from 'routing/urls'
import {
    ProductMetadataGroupBy,
    CellRenderProps,
    Label,
    CountryCode,
    ProductMetadata,
} from 'types'

import ProductCatalogTitleCell from '../ProductCatalogTitleCell/ProductCatalogTitleCell'

export function metadataIdRenderer<RecordType extends ProductMetadataGroupBy>({
    value,
    record,
    undefinedValue = UNDEFINED_VALUE,
}: CellRenderProps<RecordType>): ReactNode {
    return isUnset(value) || isEmpty(record.metadata) ? (
        undefinedValue
    ) : (
        <ResourceLink
            to={productDetailUrl(
                record.metadata.asin,
                record.metadata.marketplace
            )}
        >
            {record.metadata.title}
        </ResourceLink>
    )
}

export function productLabelIdRenderer<
    RecordType extends ProductMetadataGroupBy & {
        product__label: Label
    },
>({
    value,
    record,
    undefinedValue = UNDEFINED_VALUE,
}: CellRenderProps<RecordType>): ReactNode {
    if (isUnset(value)) {
        return undefinedValue
    }
    return (
        <ResourceLink
            to={{
                pathname: labelUrl(value),
                search: '?tab=products',
            }}
        >
            {record.product__label?.name}
        </ResourceLink>
    )
}

interface ProductInfo {
    metadata_id?: string
    product_id?: string
    asin: string
    name: string | null
    small_image_url?: string
    image_url?: string
    title: string | null
    hero_asin_image_url?: string
    hero_asin_title?: string
    marketplace?: CountryCode
    metadata?: ProductMetadata
}

const extractMarketplaceFromRecord = (
    record: ProductInfo
): CountryCode | undefined => {
    let { marketplace } = record
    if (!marketplace && record.metadata_id) {
        marketplace = record.metadata_id.split('_')[0] as CountryCode
    }
    if (!marketplace && record.product_id) {
        marketplace = record.product_id.split('_')[0] as CountryCode
    }
    return marketplace
}

export function ProductCatalogTitleRenderer<RecordType extends ProductInfo>({
    record,
    showImage,
    linkToProductDetails = false,
}: CellRenderProps<RecordType> & {
    linkToProductDetails?: boolean
}): ReactNode {
    const { metadata } = record
    let asin = metadata?.asin ?? record.asin
    if (!asin && record.product_id) {
        asin = record.product_id.split('_')[1]
    }
    return (
        <ProductCatalogTitleCell
            value={
                metadata?.hero_asin_title ??
                record.hero_asin_title ??
                metadata?.title ??
                record.title ??
                record.name
            }
            productImg={
                metadata?.hero_asin_image_url ??
                record.hero_asin_image_url ??
                metadata?.small_image_url ??
                record.small_image_url ??
                record.image_url
            }
            productAsin={asin}
            productMarketplace={extractMarketplaceFromRecord(record)}
            showImage={showImage}
            linkToProductDetails={linkToProductDetails}
        />
    )
}
